/**
 * Methods for decoding and encoding Javascript objects in the same way as the Ext.js SenseStateProvider,
 * so that settings can be shared between pages using different frameworks.
 *
 * @packageDocumentation
 */

/**
 * Encodes a value including type information.
 *
 * This is a lightly refactored copy of the implementation in SenseStateProvider.js
 * In a vacuum, I would rather use a standard format with type information, e.g. BSON or TSON, but for now I'm keeping it compatible.
 */
export function decodeExtState(value: string): unknown {
  // a -> Array
  // n -> Number
  // d -> Date
  // b -> Boolean
  // s -> String
  // o -> Object
  // e -> Empty (null)

  const re = /^([andbsoe]):(.*)$/;
  const matches = re.exec(value);

  if (!matches || !matches[1]) {
    return; // non state
  }

  const type = matches[1];
  value = matches[2];

  switch (type) {
    case 'e':
      return null;

    case 'n':
      return parseFloat(value);

    case 'd':
      return new Date(Date.parse(value));

    case 'b':
      return value === '1';
    case 'a':
      const all = [];

      if (value) {
        const values = value.split('^');

        for (const v of values) {
          all.push(decodeExtState(v));
        }
      }

      return all;

    case 'o':
      const obj = {};

      if (value) {
        const values = value.split('^');
        const vLen = values.length;

        for (let v = 0; v < vLen; v++) {
          value = values[v];
          const keyValue = value.split('=');
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          obj[keyValue[0]] = decodeExtState(keyValue[1]);
        }
      }
      return obj;

    default:
      return value;
  }
}

/**
 * Encodes a value including type information.
 *
 * This is a lightly refactored copy of the implementation in SenseStateProvider.js
 * In a vacuum, I would rather use a standard format with type information, e.g. BSON or TSON, but for now I'm keeping it compatible.
 *
 * @param {Object} value The value to encode
 * @return {String} The encoded value
 */
export function encodeExtState(value: unknown): string {
  let flat = '';
  let i = 0;
  let enc;
  let len;
  let key;

  if (value == null) {
    return 'e:1';
  } else if (typeof value === 'number') {
    enc = 'n:' + value;
  } else if (typeof value === 'boolean') {
    enc = 'b:' + (value ? '1' : '0');
    // dates
  } else if (typeof (value as Date).toISOString === 'function') {
    enc = 'd:' + (value as Date).toISOString();
    // arrays
  } else if (typeof (value as Array<unknown>).map === 'function') {
    for (len = (value as Array<unknown>).length; i < len; i++) {
      flat += encodeExtState((value as Array<unknown>)[i]);

      if (i !== len - 1) {
        flat += '^';
      }
    }

    enc = 'a:' + flat;
  } else if (typeof value === 'object') {
    for (key in value) {
      const objVal = (value as Record<string, unknown>)[key];
      if (typeof objVal !== 'function' && objVal !== undefined) {
        flat += key + '=' + encodeExtState(objVal) + '^';
      }
    }

    enc = 'o:' + flat.substring(0, flat.length - 1);
  } else {
    enc = 's:' + value;
  }

  return enc;
}
